import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppStore } from '../store/appStore';
import { useAuthStore } from '../store/authStore';
import { useRegion } from '../hooks/useRegion';
import { getPhonePlaceholder } from '../lib/regionConfig';
import { crewSuperannuationFor } from '../lib/jobFinancials';
import { Layout } from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogBody,
} from '../components/ui/dialog';
// formatCurrency now comes from useRegion hook
import { Loader2, UserPlus, Package, X } from 'lucide-react';
import { toast } from 'sonner';
import { deliverablesToHtml } from '../lib/briefHelpers';
import { createPhase, getProductionPhaseDays } from '../components/job/useJobFormHandlers';
import { UnsavedChangesDialog, useUnsavedChangesGuard } from '../components/UnsavedChangesGuard';
import { useTrackedEdits } from '../lib/useTrackedEdits';
import { buildJobPayload } from '../lib/jobPayload';
import { JobForm } from '../components/job/JobForm';

import { DEFAULT_PHASE_RATES } from '../lib/phaseRates';

export default function JobBuilderPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const briefId = searchParams.get('brief_id');
  const { createJob, getBrief, equipment, fetchEquipment, createEquipment, fetchSettings, clients, fetchClients, createClient, equipmentKits, fetchEquipmentKits, crewMembers, fetchCrewMembers } = useAppStore();
  const { checkProjectsPlanAccess, projectsPlanChecked } = useAuthStore();
  const { region, regionConfig } = useRegion();
  const [loading, setLoading] = useState(false);
  const [linkedBrief, setLinkedBrief] = useState(null);
  const deliverablesAppendedRef = useRef(false);
  const [showNewClientDialog, setShowNewClientDialog] = useState(false);
  const [newClientLoading, setNewClientLoading] = useState(false);
  const [showNewEquipmentDialog, setShowNewEquipmentDialog] = useState(false);
  const [newEquipmentLoading, setNewEquipmentLoading] = useState(false);
  const [newEquipmentForm, setNewEquipmentForm] = useState({
    name: '',
    category: 'Camera',
    daily_rate: '',
  });
  const [defaultRates, setDefaultRates] = useState(DEFAULT_PHASE_RATES);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  
  // Package dialog state
  // Active drag item for DragOverlay
  
  const [formData, setFormData] = useState({
    client: '',
    client_id: '',
    contact_email: '',
    client_job_number: '',
    client_job_name: '',
    project_name: '',
    description: '',
    brief_id: null,
    phases: [],
    crew_labour: [],
    crew_equipment: [],
    crew_superannuation: crewSuperannuationFor(region),
    equipment: [],
    equipment_packages: [],
    equipment_display_order: [], // Array of {type: 'package'|'equipment', id: string}
    expenses: [],
    hidden_costs: [],
    include_gst: false,
    gst_rate: regionConfig.tax.defaultRate,
    equipment_rental_only: false,
    rental_start_date: '',
    rental_end_date: '',
  });


  // Loads write with setFormData; everything the user does goes through
  // setTracked, so only their edits count as unsaved.
  const { dirty, setTracked } = useTrackedEdits(formData, setFormData);
  const { blocker, allowNavigation } = useUnsavedChangesGuard(dirty);

  const [newClient, setNewClient] = useState({
    business_name: '',
    billing_address: '',
    contacts: [],
  });

  // Initialize with default phase AFTER settings are loaded
  useEffect(() => {
    if (settingsLoaded && formData.phases.length === 0) {
      setFormData(prev => ({
        ...prev,
        phases: [createPhase('production', defaultRates)],
      }));
    }
  }, [settingsLoaded, defaultRates]);

  // Check Projects plan access on mount
  useEffect(() => {
    if (!projectsPlanChecked) {
      checkProjectsPlanAccess();
    }
  }, [projectsPlanChecked, checkProjectsPlanAccess]);

  useEffect(() => {
    fetchEquipment();
    fetchEquipmentKits();
    fetchClients();
    fetchCrewMembers();
    fetchSettings().then((data) => {
      if (data) {
        // The cached region set the starting value; the loaded one is authoritative.
        setFormData(prev => ({
          ...prev,
          crew_superannuation: crewSuperannuationFor(data.region, prev.crew_superannuation),
        }));
        if (data.collects_gst) {
          setFormData(prev => ({
            ...prev,
            include_gst: true,
            gst_rate: data.gst_rate || regionConfig.tax.defaultRate,
          }));
        }
        // Load description template as default description
        if (data.description_template) {
          setFormData(prev => ({
            ...prev,
            description: data.description_template,
          }));
        }
        // Load user's saved default rates
        if (data.default_phase_rates) {
          setDefaultRates({ ...DEFAULT_PHASE_RATES, ...data.default_phase_rates });
        }
      }
      setSettingsLoaded(true);
    });
  }, [fetchEquipment, fetchEquipmentKits, fetchClients, fetchCrewMembers, fetchSettings]);

  // Prefill from a brief when creating a job from one
  useEffect(() => {
    if (!briefId) return;
    (async () => {
      try {
        const brief = await getBrief(briefId);
        setLinkedBrief(brief);
        setFormData(prev => ({
          ...prev,
          brief_id: brief.id,
          project_name: brief.project_name || prev.project_name,
          client_id: brief.client_id || prev.client_id,
          client: brief.client || prev.client,
          contact_email: brief.contact_email || prev.contact_email,
        }));
      } catch (e) {
        toast.error('Could not load the linked brief');
      }
    })();
  }, [briefId, getBrief]);

  // Append the brief's deliverables to the description once the template has loaded
  useEffect(() => {
    if (!linkedBrief || !settingsLoaded || deliverablesAppendedRef.current) return;
    const html = deliverablesToHtml(linkedBrief.deliverables);
    if (!html) { deliverablesAppendedRef.current = true; return; }
    deliverablesAppendedRef.current = true;
    setFormData(prev => ({
      ...prev,
      description: prev.description ? `${prev.description}${html}` : html,
    }));
  }, [linkedBrief, settingsLoaded]);

  const handleClientSelect = (clientId) => {
    if (clientId === 'new') {
      setShowNewClientDialog(true);
      return;
    }
    const client = clients.find(c => c.id === clientId);
    if (client) {
      setTracked((prev) => ({
        ...prev,
        client_id: client.id,
        client: client.business_name,
      }));
    }
  };

  const addNewClientContact = () => {
    setNewClient({
      ...newClient,
      contacts: [...newClient.contacts, { name: '', email: '', phone: '' }],
    });
  };

  const updateNewClientContact = (index, field, value) => {
    const newContacts = [...newClient.contacts];
    newContacts[index][field] = value;
    setNewClient({ ...newClient, contacts: newContacts });
  };

  const removeNewClientContact = (index) => {
    const newContacts = newClient.contacts.filter((_, i) => i !== index);
    setNewClient({ ...newClient, contacts: newContacts });
  };

  const handleCreateClient = async () => {
    if (!newClient.business_name.trim()) {
      toast.error('Business name is required');
      return;
    }

    // Validate contacts - each contact must have an email
    for (let i = 0; i < newClient.contacts.length; i++) {
      if (!newClient.contacts[i].email || !newClient.contacts[i].email.trim()) {
        toast.error(`Contact ${i + 1}: Email is required`);
        return;
      }
    }

    setNewClientLoading(true);
    try {
      const created = await createClient(newClient);
      setTracked((prev) => ({
        ...prev,
        client_id: created.id,
        client: created.business_name,
      }));
      setShowNewClientDialog(false);
      setNewClient({ business_name: '', billing_address: '', contacts: [] });
      toast.success('Client created');
    } catch (error) {
      toast.error('Failed to create client');
    } finally {
      setNewClientLoading(false);
    }
  };

  const handleCreateEquipment = async () => {
    if (!newEquipmentForm.name.trim()) {
      toast.error('Equipment name is required');
      return;
    }
    if (!newEquipmentForm.daily_rate || parseFloat(newEquipmentForm.daily_rate) <= 0) {
      toast.error('Daily rate must be greater than 0');
      return;
    }
    setNewEquipmentLoading(true);
    try {
      const equipmentData = {
        name: newEquipmentForm.name.trim(),
        category: newEquipmentForm.category,
        daily_rate: parseFloat(newEquipmentForm.daily_rate),
        weekly_rate: newEquipmentForm.weekly_rate ? parseFloat(newEquipmentForm.weekly_rate) : null,
        is_active: true,
      };
      const created = await createEquipment(equipmentData);
      
      // Auto-add the new equipment to the current job
      setTracked(prev => ({
        ...prev,
        equipment: [...prev.equipment, {
          id: crypto.randomUUID(),
          inventory_id: created.id,
          name: created.name,
          description: created.description || '',
          quantity: 1,
          scheduled_days: getProductionPhaseDays(formData.phases),
          daily_rate: created.daily_rate,
          weekly_rate: created.weekly_rate || null,
          rate_mode: 'day',
          rate_override: null,
        }],
      }));
      
      setShowNewEquipmentDialog(false);
      setNewEquipmentForm({ name: '', category: 'Camera', daily_rate: '', weekly_rate: '' });
      toast.success(`"${created.name}" created and added to job`);
    } catch (error) {
      toast.error('Failed to create equipment');
    } finally {
      setNewEquipmentLoading(false);
    }
  };

  // Phase handlers
  // Crew Labour handlers
  // Crew Equipment handlers
  // Equipment handlers
  // Helper to get production phase days
  // Track kit selection key to force re-render of Select

  // Drag start handler for equipment/packages
  // Combined drag handler for packages and equipment in one list
  // Divider line handlers
  // Package handlers
  // Helper to check if an equipment item is in any package
  // Calculate package totals for display
  // Package maths lives in lib/jobFinancials - see calculatePackageTotal there.
  // Expense handlers
  // Hidden Cost handlers
  // Calculate totals
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const job = await createJob(buildJobPayload(formData));
      toast.success('Job created successfully');
      allowNavigation();
      navigate(`/jobs/${job.id}`);
    } catch (error) {
      toast.error('Failed to create job');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <JobForm
        mode="create"
        formData={formData}
        setFormData={setTracked}
        clients={clients}
        equipment={equipment}
        equipmentKits={equipmentKits}
        crewMembers={crewMembers}
        defaultRates={defaultRates}
        loading={loading}
        onSubmit={handleSubmit}
        onBack={() => navigate('/jobs')}
        linkedBrief={linkedBrief}
        onClientSelect={handleClientSelect}
        clientSelectExtras={
          <SelectItem value="new" className="text-primary">
            <span className="flex items-center gap-2">
              <UserPlus className="w-4 h-4" /> Create New Client
            </span>
          </SelectItem>
        }
      >
        {/* New Client Dialog */}
        <Dialog open={showNewClientDialog} onOpenChange={setShowNewClientDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Client</DialogTitle>
              <DialogDescription>
                Add a new client to your database
              </DialogDescription>
            </DialogHeader>
            <DialogBody className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="new-client-name">Business Name *</Label>
                <Input
                  id="new-client-name"
                  value={newClient.business_name}
                  onChange={(e) => setNewClient({ ...newClient, business_name: e.target.value })}
                  placeholder="Client business name"
                  data-testid="new-client-name"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="new-client-address">Billing Address</Label>
                <Input
                  id="new-client-address"
                  value={newClient.billing_address}
                  onChange={(e) => setNewClient({ ...newClient, billing_address: e.target.value })}
                  placeholder="123 Business St, Sydney NSW 2000"
                  data-testid="new-client-address"
                />
              </div>

              {/* Contacts Section */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Contacts</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addNewClientContact}
                    data-testid="add-new-client-contact-btn"
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    Add Contact
                  </Button>
                </div>

                {newClient.contacts.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4 border border-dashed rounded-lg">
                    No contacts yet. Click &quot;Add Contact&quot; to add one.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {newClient.contacts.map((contact, index) => (
                      <Card key={index} className="p-3">
                        <div className="space-y-3">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium">Contact {index + 1}</span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={() => removeNewClientContact(index)}
                              data-testid={`remove-new-client-contact-${index}`}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                          
                          <div className="space-y-2">
                            <Label className="text-xs">Name (Optional)</Label>
                            <Input
                              placeholder="Contact name"
                              value={contact.name || ''}
                              onChange={(e) => updateNewClientContact(index, 'name', e.target.value)}
                              data-testid={`new-client-contact-name-${index}`}
                            />
                          </div>

                          <div className="space-y-2">
                            <Label className="text-xs">Email *</Label>
                            <Input
                              type="email"
                              placeholder="contact@example.com"
                              value={contact.email}
                              onChange={(e) => updateNewClientContact(index, 'email', e.target.value)}
                              data-testid={`new-client-contact-email-${index}`}
                            />
                          </div>

                          <div className="space-y-2">
                            <Label className="text-xs">Phone (Optional)</Label>
                            <Input
                              placeholder={getPhonePlaceholder(region)}
                              value={contact.phone || ''}
                              onChange={(e) => updateNewClientContact(index, 'phone', e.target.value)}
                              data-testid={`new-client-contact-phone-${index}`}
                            />
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </DialogBody>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowNewClientDialog(false)}>Cancel</Button>
              <Button onClick={handleCreateClient} disabled={newClientLoading}>
                {newClientLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Create Client
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* New Equipment Dialog */}
        <Dialog open={showNewEquipmentDialog} onOpenChange={setShowNewEquipmentDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Package className="w-5 h-5" />
                Add New Equipment
              </DialogTitle>
              <DialogDescription>
                Create a new equipment item and add it to this job
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="new-equipment-name">Equipment Name *</Label>
                <Input
                  id="new-equipment-name"
                  value={newEquipmentForm.name}
                  onChange={(e) => setNewEquipmentForm({ ...newEquipmentForm, name: e.target.value })}
                  placeholder="e.g., Sony FX6, Aputure 600D"
                  data-testid="new-equipment-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-equipment-category">Category</Label>
                <Select
                  value={newEquipmentForm.category}
                  onValueChange={(value) => setNewEquipmentForm({ ...newEquipmentForm, category: value })}
                >
                  <SelectTrigger id="new-equipment-category" data-testid="new-equipment-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Camera">Camera</SelectItem>
                    <SelectItem value="Lens">Lens</SelectItem>
                    <SelectItem value="Lighting">Lighting</SelectItem>
                    <SelectItem value="Audio">Audio</SelectItem>
                    <SelectItem value="Grip">Grip</SelectItem>
                    <SelectItem value="Accessories">Accessories</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-equipment-rate">Daily Rate *</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                  <Input
                    id="new-equipment-rate"
                    type="number"
                    step="0.01"
                    min="0"
                    value={newEquipmentForm.daily_rate}
                    onChange={(e) => setNewEquipmentForm({ ...newEquipmentForm, daily_rate: e.target.value })}
                    placeholder="0.00"
                    className="pl-7 no-spinner"
                    data-testid="new-equipment-rate"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-equipment-weekly-rate">Weekly Rate <span className="text-muted-foreground font-normal text-xs">optional</span></Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                  <Input
                    id="new-equipment-weekly-rate"
                    type="number"
                    step="0.01"
                    min="0"
                    value={newEquipmentForm.weekly_rate || ''}
                    onChange={(e) => setNewEquipmentForm({ ...newEquipmentForm, weekly_rate: e.target.value })}
                    placeholder="0.00"
                    className="pl-7 no-spinner"
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowNewEquipmentDialog(false)}>Cancel</Button>
              <Button onClick={handleCreateEquipment} disabled={newEquipmentLoading}>
                {newEquipmentLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Create & Add to Job
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </JobForm>
      <UnsavedChangesDialog blocker={blocker} />
    </Layout>
  );
}
